import { Getter } from 'jotai';

import {
  Comparison,
  Condition,
  ConditionExpression,
  ConditionGroup,
  ConditionGroupOperator,
  ConditionValueKind,
  NestedConditionExpression,
} from '@repo/sdk/models/components';

import { readRefAtomValue } from '@/readings-settings/use-get-ref-value';
import { eaiControlledDeviceIdsAtom } from '@/state/eai-controlled-device-ids.state';

import { applyNegation, conditionGroupHandlers, performComparison } from './comparison-handler';
import type { ConditionalState } from './types';

/** Reads the source value for a condition's ref reactively via Jotai `get`. */
const getSourceValue = (get: Getter, condition: Condition): any => {
  return condition.ref ? readRefAtomValue(get, condition.ref) : undefined;
};

/** Extracts the static target value from a condition's {@link ConditionValueKind}. */
export const getTargetValue = (condition: Condition): any => {
  const { value } = condition;
  switch (value.kind) {
    case ConditionValueKind.Boolean:
      return value.boolean;
    case ConditionValueKind.Number:
      return value.number;
    case ConditionValueKind.Enum:
      return value.enum;
    case ConditionValueKind.Schedule:
      return value.schedule;
    case ConditionValueKind.Sequence:
      return value.sequence;
    case ConditionValueKind.String:
      return value.string;
    case ConditionValueKind.EnumArray:
      return value.enumArray;
    default:
      console.warn(`Unknown value kind: ${value.kind}`);
      return null;
  }
};

/**
 * Evaluates a single condition reactively.
 *
 * - Resolves source and target values through Jotai `get`
 * - Handles `IsUnderEaiControl` via `get(eaiControlledDeviceIdsAtom)`
 * - Resolves `secondaryRef` (used by `IsTrimAtCurrentStage`) reactively
 * - Applies negate: `condition.negate ? !result : result`
 */
export const evaluateCondition = (get: Getter, condition: Condition): boolean => {
  try {
    let result: boolean;

    if (condition.comparison === Comparison.IsUnderEaiControl) {
      const targetValue = getTargetValue(condition);
      result = get(eaiControlledDeviceIdsAtom)?.some((id) => id === targetValue) ?? false;
    } else {
      const sourceValue = getSourceValue(get, condition);
      if (sourceValue === null || sourceValue === undefined) {
        return false;
      }

      const targetValue = getTargetValue(condition);
      if (targetValue === null || targetValue === undefined) {
        const nilComparisons = [Comparison.IsNilOrEmpty, Comparison.IsNotNilOrEmpty] as const;
        const needsTargetValue = !nilComparisons.includes(condition.comparison as any);
        if (needsTargetValue) {
          return false;
        }
      }

      const secondaryValue = condition.secondaryRef
        ? readRefAtomValue(get, condition.secondaryRef)
        : undefined;

      result = performComparison(condition, sourceValue, targetValue, secondaryValue);
    }

    return applyNegation(result, condition.negate);
  } catch (error) {
    console.error('Error evaluating condition:', error);
    return false;
  }
};

/** Evaluates a group of conditions combined by a logical AND/OR operator. */
export const evaluateConditionGroup = (get: Getter, conditionGroup: ConditionGroup): boolean => {
  const { conditions, operator, negate } = conditionGroup;
  if (!operator) {
    console.warn('No operator found in condition group');
    return false;
  }
  const handler = conditionGroupHandlers[operator as ConditionGroupOperator];
  const result = handler((conditions ?? []).map((condition) => evaluateCondition(get, condition)));
  return applyNegation(result, negate);
};

/** Evaluates a nested expression — groups of condition groups combined by a logical operator. */
export const evaluateNestedExpression = (
  get: Getter,
  nestedExpression: NestedConditionExpression
): boolean => {
  const { operator, groups, negate } = nestedExpression;
  if (!operator || !groups || groups.length === 0) {
    return false;
  }
  const handler = conditionGroupHandlers[operator as ConditionGroupOperator];
  const result = handler(groups.map((group) => evaluateConditionGroup(get, group)));
  return applyNegation(result, negate);
};

/** Dispatches evaluation to the correct handler based on expression shape (condition, group, or nested). */
export const evaluateConditionExpression = (
  get: Getter,
  expression: ConditionExpression | undefined
): boolean => {
  if (!expression) {
    return false;
  }
  if (expression.condition) {
    return evaluateCondition(get, expression.condition);
  }
  if (expression.conditionGroup) {
    return evaluateConditionGroup(get, expression.conditionGroup);
  }
  if (expression.nestedExpression) {
    return evaluateNestedExpression(get, expression.nestedExpression);
  }
  return false;
};

/**
 * Filters an array of states by evaluating their condition expressions, then
 * returns the highest-priority match (lowest `priority` number).
 *
 * - States with no `conditionExpression` pass only if `isDefault` is true.
 * - States marked `isDefault` with a `conditionExpression` always pass.
 * - All other states pass only if their expression evaluates to `true`.
 */
export const evaluateStates = <TState extends ConditionalState>(
  get: Getter,
  states: TState[]
): TState | undefined => {
  return states
    .filter((state) => {
      if (!state.conditionExpression) {
        return state.isDefault || false;
      }
      if (state.isDefault) {
        return true;
      }
      return evaluateConditionExpression(get, state.conditionExpression);
    })
    .sort((a, b) => {
      // Default states sort last (Infinity) so explicit priorities always win.
      const aPriority = a.isDefault ? Infinity : (a.priority ?? 0);
      const bPriority = b.isDefault ? Infinity : (b.priority ?? 0);
      return aPriority - bPriority;
    })[0];
};
